import React, { useRef, useState } from 'react';
import { Button } from '../ui/button';
import { X, Twitter, Download, Share2, Loader2, Trophy, Gamepad2, Clock, DollarSign } from 'lucide-react';
import html2canvas from 'html2canvas';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChallengeShareCard = ({ challenge, onClose }) => {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  const isLive = challenge.status === 'active';
  const isScheduled = challenge.status === 'scheduled';

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const generateAndShare = async () => {
    if (!cardRef.current) return;
    
    setGenerating(true);
    try {
      // Generate image from the card
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#0f0f1a',
        logging: false,
        useCORS: true,
        allowTaint: true
      });
      
      const imageData = canvas.toDataURL('image/jpeg', 0.9);
      
      // Upload to backend
      const response = await axios.post(`${API}/challenge-share/upload`, {
        image_data: imageData,
        challenge_id: challenge.id,
        challenge_title: challenge.title,
        game_name: challenge.game_name,
        prize: `$${challenge.prize_amount}`,
        status: challenge.status,
        end_time: challenge.end_time
      });
      
      const fullShareUrl = `${window.location.origin}/api/challenge-share/${response.data.share_id}`;
      setShareUrl(fullShareUrl);
      
      toast.success('Share card generated!');
    } catch (error) {
      console.error('Failed to generate share card:', error);
      toast.error('Failed to generate share card');
    } finally {
      setGenerating(false);
    }
  };

  const shareToTwitter = () => {
    const statusText = isLive ? '🚀 LIVE NOW' : isScheduled ? '📅 COMING SOON' : '';
    const text = `${statusText} ${challenge.title}\n\n🎮 ${challenge.game_name || 'Gaming Challenge'}\n💰 $${challenge.prize_amount} Prize\n\nSubmit your best gameplay videos!\n\n`;
    const url = shareUrl || 'https://cesarmchallenges.dev';
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const downloadImage = async () => {
    if (!cardRef.current) return;
    
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: '#0f0f1a',
        logging: false,
        useCORS: true,
        allowTaint: true
      });
      
      const link = document.createElement('a');
      link.download = `${challenge.title.replace(/\s+/g, '-')}-challenge.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Image downloaded!');
    } catch (error) {
      toast.error('Failed to download image');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm">
      <div className="relative max-w-2xl w-full">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Card Preview */}
        <div 
          ref={cardRef}
          className="w-full rounded-2xl overflow-hidden shadow-2xl"
          style={{ 
            background: 'linear-gradient(135deg, #0a0a0f 0%, #1a0a20 50%, #0f0a1a 100%)',
            aspectRatio: '1200/630',
            minHeight: '315px'
          }}
        >
          <div className="h-full relative overflow-hidden">
            {/* Background effects */}
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-red-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl" />
            
            <div className="h-full flex relative z-10">
              {/* Left side - Game cover */}
              <div className="w-1/3 relative flex-shrink-0">
                {challenge.game_cover_url ? (
                  <>
                    <img 
                      src={challenge.game_cover_url} 
                      alt={challenge.game_name}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/70" />
                  </>
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-red-500/20 to-purple-500/20 flex items-center justify-center">
                    <Gamepad2 className="w-20 h-20 text-white/20" />
                  </div>
                )}
                
                {/* Game name */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="bg-black/80 rounded-lg px-3 py-2">
                    <p className="text-white font-bold text-sm truncate">{challenge.game_name || 'Gaming'}</p>
                  </div>
                </div>
              </div>

              {/* Right side - Challenge info */}
              <div className="flex-1 p-6 flex flex-col justify-between">
                {/* Status badge */}
                <div>
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-bold ${
                    isLive 
                      ? 'bg-green-500 text-white' 
                      : isScheduled 
                        ? 'bg-orange-500 text-white' 
                        : 'bg-gray-600 text-white'
                  }`}>
                    {isLive ? '🔴 LIVE NOW' : isScheduled ? '📅 COMING SOON' : '✅ ENDED'}
                  </span>
                </div>

                {/* Title & Prize */}
                <div className="py-4">
                  <h1 
                    className="text-3xl font-black text-white mb-4 leading-tight"
                    style={{ fontFamily: 'Orbitron, sans-serif' }}
                  >
                    {challenge.title}
                  </h1>
                  
                  {/* Prize */}
                  <div className="flex items-center gap-3 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 w-fit">
                    <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-7 h-7 text-black" />
                    </div>
                    <div>
                      <p className="text-yellow-500/70 text-xs uppercase">Prize Pool</p>
                      <p className="text-3xl font-black text-yellow-400">${challenge.prize_amount}</p>
                    </div>
                  </div>
                </div>

                {/* Timer & Branding */}
                <div className="flex items-center justify-between">
                  {challenge.end_time ? (
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <Clock className="w-4 h-4 text-red-400" />
                      <span>Ends {formatDate(challenge.end_time)}</span>
                    </div>
                  ) : <div />}
                  
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-red-500" />
                    <span className="text-gray-500 text-sm font-medium">cesarmchallenges.dev</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          {!shareUrl ? (
            <Button 
              onClick={generateAndShare}
              disabled={generating}
              className="bg-blue-500 hover:bg-blue-600 gap-2"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4" />
                  Generate Share Link
                </>
              )}
            </Button>
          ) : (
            <Button 
              onClick={shareToTwitter}
              className="bg-black hover:bg-gray-900 gap-2 border border-gray-700"
            >
              <Twitter className="w-4 h-4" />
              Share on X/Twitter
            </Button>
          )}
          
          <Button 
            variant="outline"
            onClick={downloadImage}
            className="border-gray-600 gap-2"
          >
            <Download className="w-4 h-4" />
            Download Image
          </Button>

          {shareUrl && (
            <Button 
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(shareUrl);
                toast.success('Link copied!');
              }}
              className="border-gray-600 gap-2"
            >
              Copy Link
            </Button>
          )}
        </div>

        {shareUrl && (
          <p className="mt-4 text-center text-sm text-gray-500">
            Share URL: <span className="text-blue-400">{shareUrl}</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default ChallengeShareCard;
